import { useHistory } from 'react-router-dom'
import { client } from '../../hooks/Client'
import EmptyStateBloom from './EmptyStateBloom'
import { getEmptyStateConfig } from './dashboardEmptyStateConfig'

// AI-CONTEXT: See docs/features/dashboard-empty-states.md

const ClockIcon = () => (
    <svg width='22' height='22' viewBox='0 0 24 24' fill='none'>
        <circle cx='12' cy='12' r='8.4' stroke='currentColor' strokeWidth='1.6' />
        <path d='M12 7.6V12l3 1.8' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
)

const SparkIcon = () => (
    <svg width='22' height='22' viewBox='0 0 24 24' fill='none'>
        <path d='M12 4l1.4 4.6L18 10l-4.6 1.4L12 16l-1.4-4.6L6 10l4.6-1.4L12 4z' stroke='currentColor' strokeWidth='1.5' strokeLinejoin='round' />
    </svg>
)

const ChartIcon = () => (
    <svg width='22' height='22' viewBox='0 0 24 24' fill='none'>
        <path d='M5 19V9M12 19V5M19 19v-6' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' />
    </svg>
)

const ICONS = { clock: ClockIcon, chart: ChartIcon, spark: SparkIcon }

/**
 * Standardized "no data" state for dashboard cards (design variant A+D).
 *
 * Renders an icon, a headline, a supporting sentence and an optional CTA over
 * a soft ghost-chart background. Copy + CTA come from the per-type config in
 * `dashboardEmptyStateConfig`, with optional per-instance overrides (used for
 * the filter-empty variant).
 *
 * @param {object} props
 * @param {string} [props.type] - Card type key used to look up the default copy/CTA.
 * @param {'clock'|'chart'|'spark'} [props.icon] - Override the icon.
 * @param {string} [props.headline] - Override the headline.
 * @param {string} [props.subtext] - Override the supporting sentence.
 * @param {({ label: string, path?: string } | null)} [props.cta] - Override the CTA (pass `null` to hide it).
 * @param {() => void} [props.onCtaClick] - Custom CTA handler. When omitted, navigates to `/${client}/${cta.path}`.
 */
const DashboardCardEmptyState = ({ type, icon, headline, subtext, cta, onCtaClick }) => {
    const history = useHistory()
    const base = getEmptyStateConfig(type)

    const resolvedIcon = icon || base.icon
    const resolvedHeadline = headline || base.headline
    const resolvedSubtext = subtext || base.subtext
    const resolvedCta = cta !== undefined ? cta : base.cta

    const IconComponent = ICONS[resolvedIcon] || ChartIcon

    const handleCtaClick = () => {
        if (onCtaClick) {
            onCtaClick()
            return
        }
        if (resolvedCta?.path) {
            history.push(`/${client}/${resolvedCta.path}`)
        }
    }

    return (
        <div className='dashboard-empty-state'>
            <EmptyStateBloom />
            <div className='dashboard-empty-state-content'>
                <div className='dashboard-empty-state-icon'>
                    <IconComponent />
                </div>
                <p className='dashboard-empty-state-msg'>{resolvedHeadline}</p>
                <p className='dashboard-empty-state-sub'>{resolvedSubtext}</p>
                {resolvedCta && (
                    <button
                        type='button'
                        className='dashboard-empty-state-cta'
                        onClick={handleCtaClick}
                    >
                        {resolvedCta.label}
                    </button>
                )}
            </div>
        </div>
    )
}

export default DashboardCardEmptyState
